import { useRef } from 'react';
import { toastError, toastSuccess } from '../../utils/toast';

const MAX_BYTES = 256 * 1024;

function isAcceptedFile(file) {
    if (!file) return false;
    const name = file.name?.toLowerCase() ?? '';
    if (name.endsWith('.csv')) return true;
    if (file.type === 'text/csv' || file.type === 'text/plain') return true;
    return false;
}

export default function Step1Paste({
    csvText,
    setCsvText,
    error,
    loading,
    onCancel,
    onValidate,
}) {
    const fileInputRef = useRef(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        if (!isAcceptedFile(file)) {
            toastError('Pick a .csv file.');
            return;
        }

        if (file.size > MAX_BYTES) {
            toastError('CSV is too large (256 KB max).');
            return;
        }

        try {
            const raw = await file.text();
            const normalized = raw
                .replace(/^\uFEFF/, '')
                .replace(/\r\n/g, '\n')
                .replace(/\r/g, '\n');
            setCsvText(normalized);
            toastSuccess('CSV loaded — review and click Validate & continue.');
        } catch {
            toastError('Could not read that file.');
        }
    };

    return (
        <div className="bulk-state">
            <div className="bulk-state-title">Step 1 of 4</div>
            <h3>Paste CSV</h3>
            <div className="bulk-paste-grid">
                <div className="csv-upload">
                    <button
                        type="button"
                        className="btn btn-primary csv-upload-btn"
                        onClick={handleUploadClick}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Upload CSV
                    </button>
                    <p className="csv-upload-hint">…or paste your CSV directly →</p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        className="csv-upload-input"
                        onChange={handleFileChange}
                        aria-label="Upload CSV file"
                    />
                </div>
                <textarea
                    className="csv-paste"
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                    placeholder="Paste CSV here…"
                    aria-label="Paste CSV"
                />
            </div>

            {error && (
                <div className="bulk-alert-error" role="alert">{error}</div>
            )}

            <div className="bulk-actions">
                <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!csvText.trim() || loading}
                    onClick={onValidate}
                >
                    {loading ? 'Validating…' : 'Validate & continue'}
                </button>
            </div>
        </div>
    );
}
